import { useTheme } from '@material-ui/core';
import React, { useState } from 'react';

import Camera from '@portal/assets/svg/profile/ic_camera.svg?react';
import User from '@portal/assets/svg/profile/user.svg?react';

import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { translate } from '@portal/services/i18n';
import { maskCpfOrCnpj, maskPhone } from '@portal/services/masks';
import { standardDateLong } from '@portal/utils/format';

import ThemedText from '@portal/components/ThemedText/ThemedText';
import { myAccount } from '@portal/config/app.json';
import useReduxState from '@portal/hooks/useReduxState';

interface ProfileInformationProps {
  name?: string;
  email?: string;
  cellphone?: string;
  taxIdentifier?: string;
  motherName?: string;
  bornAt?: string;
  photoUrl?: string;
  hideCamera?: boolean;
  accountType?: AccountTaxIdentifierType | string;
  account?: string;
  onChangePictureClick?: React.ChangeEventHandler<HTMLInputElement>;
  branch?: string;
  referralCode?: string;
  bankNumber?: string;
}

const ProfileInformation = ({
  name,
  email,
  cellphone,
  taxIdentifier,
  motherName,
  bornAt,
  photoUrl,
  hideCamera = false,
  account,
  onChangePictureClick,
  accountType,
  branch,
  referralCode,
  bankNumber,
}: ProfileInformationProps) => {
  const {
    palette: { primary },
  } = useTheme();
  const {
    theme: { currentTheme },
  } = useReduxState();

  const [imageError, setImageError] = useState(false);

  const renderInfoItem = () => {
    const info = [
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.NAME'),
        visible: name,
        value: name,
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.EMAIL'),
        visible: email,
        value: email,
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.CELLPHONE'),
        visible: cellphone,
        value: maskPhone(cellphone || ''),
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.TAX_IDENTIFIER'),
        visible: taxIdentifier,
        value: maskCpfOrCnpj(taxIdentifier || ''),
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.MOTHER_NAME'),
        visible: motherName,
        value: motherName,
      },
      {
        label:
          accountType === AccountTaxIdentifierType.CORPORATE
            ? translate('PAGES.MY_ACCOUNT.PROFILE.PJ_BIRTHDAY')
            : translate('PAGES.MY_ACCOUNT.PROFILE.BIRTHDAY'),
        visible: bornAt,
        value: standardDateLong(bornAt || ''),
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.REFERRAL_CODE'),
        visible: referralCode,
        value: referralCode,
      },
    ];

    return info
      .filter((item) => item.visible)
      .map((item) => (
        <div
          className="profile-information__section__grid__item"
          key={item.label}
        >
          <ThemedText className="profile-information__section__grid__item__label">
            {item.label}
          </ThemedText>
          <ThemedText className="profile-information__section__grid__item__value">
            {item.value}
          </ThemedText>
        </div>
      ));
  };

  const renderInfoBankItem = () => {
    const info = [
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.BANK_NUMBER'),
        visible: bankNumber,
        value: bankNumber,
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.BRANCH'),
        visible: branch,
        value: branch,
      },
      {
        label: translate('PAGES.MY_ACCOUNT.PROFILE.BANK_ACCOUNT'),
        visible: account,
        value: myAccount.accountDigit
          ? `${account?.substring(0, account?.length - 1)}-${account?.slice(
              -1
            )}`
          : account,
      },
    ];

    return info
      .filter((item) => item.visible)
      .map((item) => (
        <div
          className="profile-information__section__grid__item"
          key={item.label}
        >
          <ThemedText className="profile-information__section__grid__item__label">
            {item.label}
          </ThemedText>
          <ThemedText className="profile-information__section__grid__item__value">
            {item.value}
          </ThemedText>
        </div>
      ));
  };

  const hasPersonalInfo =
    name ||
    email ||
    cellphone ||
    taxIdentifier ||
    motherName ||
    bornAt ||
    referralCode;
  const hasBankData = branch || account || bankNumber;

  const renderEmptyState = () => (
    <div className="profile-empty-state">
      <div className="empty-state-icon">
        <User />
      </div>
      <ThemedText variant="h5" className="empty-state-title">
        {translate('PAGES.MY_ACCOUNT.PROFILE.EMPTY_STATE.TITLE')}
      </ThemedText>
      <ThemedText variant="body" className="empty-state-description">
        {translate('PAGES.MY_ACCOUNT.PROFILE.EMPTY_STATE.DESCRIPTION')}
      </ThemedText>
    </div>
  );

  const renderProfileAvatar = () => {
    if (!imageError && photoUrl) {
      return (
        <img
          src={photoUrl}
          className="profile-avatar-img"
          alt="profile_image"
          onError={() => setImageError(true)}
        />
      );
    }

    return (
      <div>
        <User />
      </div>
    );
  };

  const renderHeaderProfileAvatar = () => (
    <div
      className="profile-information__card"
      style={{ borderLeft: `4px solid ${currentTheme.borderColor}` }}
    >
      <div className="profile-information__card__avatar">
        <div className="profile-information__card__avatar__icon">
          {renderProfileAvatar()}
          {!hideCamera && (
            <div className="profile-information__card__avatar__camera">
              <label
                htmlFor="file"
                className="profile-information__card__avatar__camera__button"
              >
                <Camera fill={primary.main} width={18} height={18} />
              </label>
              <input
                title="profile"
                id="file"
                type="file"
                className="file-input"
                accept="image/png, image/jpeg"
                onChange={onChangePictureClick}
              />
            </div>
          )}
        </div>
      </div>
      <div className="profile-information__card__info">
        <ThemedText variant="h5" className="user-name">
          {name}
        </ThemedText>
        <ThemedText variant="body" className="user-email">
          {email}
        </ThemedText>
      </div>
    </div>
  );

  if (!hasPersonalInfo && !hasBankData) {
    return (
      <div className="profile-information">
        {renderHeaderProfileAvatar()}
        {renderEmptyState()}
      </div>
    );
  }

  return (
    <div className="profile-information">
      {renderHeaderProfileAvatar()}
      {hasPersonalInfo ? (
        <div
          className="profile-information__section"
          style={{ borderLeft: `4px solid ${currentTheme.borderColor}` }}
        >
          <ThemedText
            variant="h5"
            className="profile-information__section__header"
          >
            {translate('PAGES.MY_ACCOUNT.TABS.PERSONAL_INFORMATION')}
          </ThemedText>
          <div className="profile-information__section__grid">
            {renderInfoItem()}
          </div>
        </div>
      ) : null}
      {hasBankData ? (
        <div
          className="profile-information__section"
          style={{ borderLeft: `4px solid ${currentTheme.borderColor}` }}
        >
          <ThemedText
            variant="h5"
            className="profile-information__section__header"
          >
            {translate('PAGES.MY_ACCOUNT.PROFILE.BANK_DATA')}
          </ThemedText>
          <div className="profile-information__section__grid">
            {renderInfoBankItem()}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProfileInformation;
